import React, { createContext, useContext, useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase";
import { clearActiveUserStorage, hasLocalUserStorage, initializeUserStorage } from "../lib/userStorage";
import { reconcileGeneralProgressSchedule } from "../helpers/generalProgressSchedule";

const AuthContext = createContext(null);
const STORAGE_BOOT_TIMEOUT = 3000;

function waitForStorageBoot(syncPromise) {
  return Promise.race([
    syncPromise,
    new Promise((resolve) => setTimeout(() => resolve({ source: "timeout" }), STORAGE_BOOT_TIMEOUT)),
  ]);
}

/**
 * useAuth — Hook to access auth state and methods from any component.
 *
 * Provides:
 *   user         — Firebase User object (or null)
 *   loading      — true while initial auth state is being resolved
 *   signup       — (email, password) → Promise
 *   login        — (email, password) → Promise
 *   loginWithGoogle — () => Promise
 *   resetPassword — (email) → Promise
 *   logout       — () => Promise
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/**
 * AuthProvider — Wraps the app to provide auth state and helpers.
 * Persists session across refreshes via Firebase's onAuthStateChanged.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Listen for auth state changes (login, logout, page refresh)
  useEffect(() => {
    let authTransition = 0;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const transition = ++authTransition;
      setLoading(true);
      if (firebaseUser) {
        const hasLocalCache = hasLocalUserStorage(firebaseUser.uid);
        const syncPromise = initializeUserStorage(firebaseUser.uid);

        if (hasLocalCache) {
          reconcileGeneralProgressSchedule();
        } else {
          await waitForStorageBoot(syncPromise);
        }

        if (transition !== authTransition) return;
        setUser(firebaseUser);
        setLoading(false);

        void syncPromise.then(() => {
          if (transition === authTransition && !hasLocalCache) {
            reconcileGeneralProgressSchedule();
          }
        });
        return;
      } else {
        clearActiveUserStorage();
      }
      if (transition !== authTransition) return;
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // ── Auth methods ──

  const signup = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const loginWithGoogle = () => signInWithPopup(auth, googleProvider);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const logout = () => signOut(auth);

  const value = { user, loading, signup, login, loginWithGoogle, resetPassword, logout };

  if (loading) return null;

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
